import { useEffect, useState } from "react";
import {
  getAllPromoters, getSalesByMonth, getAllTargets,
  getPendingSales, approveSale, rejectSale,
  getCancelRequests, approveCancelSale, rejectCancelSale, updateSaleHighend,
  revertSaleApproval, getSalesBySerials,
} from "../../firebase/services";
import { calcSalesBonus, calcHighendBonus } from "../../firebase/schema";
import { isMonthEditable } from "../promoter/PromoterSales";
import { usePayrollConfig } from "../../hooks/usePayrollConfig";
import { useSalesSort } from "../../hooks/useSalesSort";
import { format } from "date-fns";
import { useLanguage } from "../../hooks/useLanguage";

export default function AdminSales() {
  const { t } = useLanguage();
  const [tab, setTab] = useState("progress");
  const [month, setMonth] = useState(format(new Date(), "yyyy-MM"));
  const [promoters, setPromoters] = useState([]);
  const [salesList, setSalesList] = useState([]);
  const [targetMap, setTargetMap] = useState({});
  const [pending, setPending] = useState([]);
  const { cfgFor } = usePayrollConfig();
  const bonusCfg = cfgFor(month).bonus;   // quy tắc thưởng áp dụng cho tháng đang xem
  const [cancelReqs, setCancelReqs] = useState([]);
  const [dupMap, setDupMap] = useState({}); // serial → [đơn khác cùng serial]

  const load = async () => {
    const [proms, sales, targets, pend, cancels] = await Promise.all([
      getAllPromoters(),
      getSalesByMonth(month),
      getAllTargets(month),
      getPendingSales(),
      getCancelRequests(),
    ]);
    setPromoters(proms);
    setSalesList(sales);
    const tm = {};
    targets.forEach(t => { tm[t.promoterId] = t; });
    setTargetMap(tm);
    setPending(pend);
    setCancelReqs(cancels);
    // Cảnh báo serial trùng cho các đơn chờ duyệt
    if (pend.length > 0) {
      getSalesBySerials(pend.map(s => s.serial)).then(setDupMap).catch(() => setDupMap({}));
    } else {
      setDupMap({});
    }
  };

  const handleApproveCancel = async (s) => {
    // Tháng đã qua hạn chốt sổ: duyệt hủy sẽ làm đổi doanh số và thưởng của tháng đã tính lương
    if (!isMonthEditable(s.month) && !window.confirm(t("sales.locked_approve_warn", { month: s.month }))) return;
    await approveCancelSale(s.id, "Admin", "admin");
    load();
  };
  // Từ chối hủy không đụng tới số liệu (đơn vẫn giữ nguyên trạng thái đã duyệt)
  // nên luôn cho phép, kể cả tháng đã khóa — nếu chặn thì yêu cầu sẽ kẹt vĩnh viễn.
  const handleRejectCancel = async (s) => { await rejectCancelSale(s.id, "Admin", "admin"); load(); };

  const handleRevertApproval = async (s) => {
    if (!window.confirm(t("sales.revert_confirm"))) return;
    await revertSaleApproval(s.id, "Admin", "admin");
    load();
  };

  const STATUS_VI = { approved: "đã duyệt", pending: "chờ duyệt", rejected: "bị từ chối", cancelled: "đã hủy" };
  const dupOf = (s) => (dupMap[s.serial] || []).filter(d => d.id !== s.id);

  const [search, setSearch] = useState("");
  const q = search.trim().toLowerCase();
  const filteredDetail = q
    ? salesList.filter(s =>
        (s.promoterName || "").toLowerCase().includes(q) ||
        (s.model || "").toLowerCase().includes(q) ||
        (s.serial || "").toLowerCase().includes(q))
    : salesList;
  const { sorted: sortedDetail, toggle: sortToggle, arrow: sortArrow } = useSalesSort(filteredDetail);

  const handleToggleHighend = async (s) => {
    await updateSaleHighend(s.id, !s.isHighend, "Admin", "admin");
    load();
  };

  useEffect(() => { load(); }, [month]);

  const getStats = (uid) => {
    const sold = salesList.filter(s => s.promoterId === uid).length;
    const highendSold = salesList.filter(s => s.promoterId === uid && s.isHighend).length;
    const t = targetMap[uid];
    const target = t?.sellTarget || 0;
    const highendTarget = t?.highendTarget || 0;
    const hitRate = target > 0 ? sold / target : 0;
    const heHitRate = highendTarget > 0 ? highendSold / highendTarget : 0;
    const bonus = calcSalesBonus(sold, target, bonusCfg?.salesTiers);
    const heBonus = calcHighendBonus(highendSold, highendTarget, bonusCfg?.highendConfig);
    return { sold, highendSold, target, highendTarget, hitRate, heHitRate, bonus, heBonus };
  };

  const exportSalesCSV = () => {
    const promoterMap = Object.fromEntries(promoters.map(p => [p.uid, p]));
    const header = ["Tháng", "Promoter", "Shop", "Mã model", "Số serial", "Cao cấp", "Ngày bán", "Người duyệt", "Vai trò"];
    const rows = salesList.map(s => [
      s.month, s.promoterName,
      promoterMap[s.promoterId]?.shopName || "",
      s.model, s.serial,
      s.isHighend ? "Có" : "Không",
      s.saleDate, s.approvedBy || "", s.approverRole || "",
    ]);
    const csv = [header, ...rows]
      .map(r => r.map(v => `"${String(v ?? "").replace(/"/g, '""')}"`).join(","))
      .join("\n");
    const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `doanh-so_${month}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  const handleApproveAll = async () => {
    if (pending.length === 0) return;
    await Promise.all(pending.map(s => approveSale(s.id, "Admin", "admin")));
    load();
  };

  const hitColor = (r) => r >= 1.2 ? "#534AB7" : r >= 1.0 ? "#C8152A" : r >= 0.8 ? "#1D9E75" : r >= 0.6 ? "#EF9F27" : "#E24B4A";
  const hitBadge = (r) => {
    if (r >= 1.2) return "badge-purple";
    if (r >= 1.0) return "badge-blue";
    if (r >= 0.8) return "badge-green";
    return "badge-red";
  };

  return (
    <>
      <div className="page-header">
        <div><h1>{t("sales.title")}</h1><p>{t("sales.subtitle")}</p></div>
        <input type="month" value={month} onChange={e => setMonth(e.target.value)} style={{ width: 160 }} />
      </div>

      <div className="tabs">
        {[["progress", t("sales.tab_progress")], ["pending", `${t("sales.tab_pending")} ${pending.length > 0 ? "("+pending.length+")" : ""}`], ["cancel", `${t("sales.tab_cancel")} ${cancelReqs.length > 0 ? "("+cancelReqs.length+")" : ""}`], ["detail", t("sales.tab_detail")]].map(([key, label]) => (
          <div key={key} className={"tab" + (tab === key ? " active" : "")} onClick={() => setTab(key)}>{label}</div>
        ))}
      </div>

      {tab === "progress" && (
        <div className="card">
          <table className="table-card">
            <thead>
              <tr>
                <th>{t("sales.promoter")}</th><th>{t("sales.shop")}</th>
                <th>{t("sales.target_ds")}</th><th>{t("sales.sold")}</th><th>{t("sales.hit_rate")}</th><th>{t("sales.progress")}</th>
                <th>{t("sales.target_he")}</th><th>{t("sales.he_sold")}</th>
                <th>{t("sales.bonus_ds")}</th><th>{t("sales.bonus_he")}</th>
              </tr>
            </thead>
            <tbody>
              {promoters.map(p => {
                const s = getStats(p.uid);
                const pct = s.target > 0 ? Math.min(100, Math.round(s.hitRate * 100)) : 0;
                const hePct = s.highendTarget > 0 ? Math.min(100, Math.round(s.heHitRate * 100)) : 0;
                return (
                  <tr key={p.uid}>
                    <td data-label="Promoter"><strong>{p.name}</strong></td>
                    <td data-label="Shop" style={{ color: "#888", fontSize: 12 }}>{p.shopName}</td>
                    <td data-label="Target DS">{s.target || <span style={{ color: "#bbb" }}>—</span>}</td>
                    <td data-label="Đã bán">{s.sold}</td>
                    <td data-label="Hit rate"><span className={"badge " + hitBadge(s.hitRate)}>{Math.round(s.hitRate * 100)}%</span></td>
                    <td data-label="Tiến độ" style={{ minWidth: 120 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <div className="progress-wrap">
                          <div className="progress-bar" style={{ width: pct + "%", background: hitColor(s.hitRate) }} />
                        </div>
                        <span style={{ fontSize: 11, color: "#888", minWidth: 32 }}>{pct}%</span>
                      </div>
                    </td>
                    <td data-label="Target HE">{s.highendTarget || <span style={{ color: "#bbb" }}>—</span>}</td>
                    <td data-label="HE đã bán">
                      <span style={{ fontWeight: 500, color: s.highendSold >= s.highendTarget && s.highendTarget > 0 ? "#C8152A" : undefined }}>
                        {s.highendSold}
                      </span>
                      {s.highendTarget > 0 && (
                        <span style={{ fontSize: 11, color: "#aaa", marginLeft: 4 }}>/ {s.highendTarget}</span>
                      )}
                    </td>
                    <td data-label="Thưởng DS">{s.bonus > 0 ? s.bonus.toLocaleString("vi-VN") + " đ" : <span className="badge badge-gray">{t("sales.not_reached")}</span>}</td>
                    <td data-label="Thưởng HE">{s.heBonus > 0 ? s.heBonus.toLocaleString("vi-VN") + " đ" : "—"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {tab === "pending" && (
        <div className="card">
          {pending.length > 0 && (
            <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 12 }}>
              <button className="btn btn-primary btn-sm" onClick={handleApproveAll}>
                ✓ {t("sales.approve_all")} ({pending.length})
              </button>
            </div>
          )}
          {pending.length === 0 ? (
            <div style={{ color: "#aaa", textAlign: "center", padding: 24 }}>{t("sales.no_pending")}</div>
          ) : (
            <table className="table-card">
              <thead>
                <tr><th>{t("sales.promoter")}</th><th>{t("sales.model")}</th><th>{t("sales.serial")}</th><th>{t("sales.highend")}</th><th>{t("sales.sale_date")}</th><th>{t("sales.submit_date")}</th><th></th></tr>
              </thead>
              <tbody>
                {pending.map(s => (
                  <tr key={s.id}>
                    <td data-label="Promoter">{s.promoterName}</td>
                    <td data-label="Model">{s.model}</td>
                    <td data-label="Serial" style={{ fontFamily: "monospace", fontSize: 12 }}>
                      {s.serial}
                      {dupOf(s).length > 0 && (
                        <div style={{ fontFamily: "inherit", fontSize: 11, color: "#B91C1C", marginTop: 4, fontWeight: 600 }}>
                          {t("sales.dup_serial")}
                          {dupOf(s).slice(0, 2).map(d => (
                            <div key={d.id} style={{ fontWeight: 400 }}>
                              • {d.promoterName} · {d.saleDate} · {STATUS_VI[d.status] || d.status}
                            </div>
                          ))}
                          {dupOf(s).length > 2 && <div style={{ fontWeight: 400 }}>… +{dupOf(s).length - 2} {t("sales.dup_others")}</div>}
                        </div>
                      )}
                    </td>
                    <td data-label="Highend">{s.isHighend ? <span className="badge badge-blue">Có</span> : <span className="badge badge-gray">Không</span>}</td>
                    <td data-label="Ngày bán">{s.saleDate}</td>
                    <td data-label="Ngày nộp" style={{ color: "#888", fontSize: 12 }}>
                      {s.submittedAt?.toDate ? format(s.submittedAt.toDate(), "dd/MM HH:mm") : "—"}
                    </td>
                    <td data-label="">
                      <div style={{ display: "flex", gap: 6 }}>
                        <button className="btn btn-primary btn-sm" onClick={async () => { await approveSale(s.id, "Admin", "admin"); load(); }}>{t("common.approve_short")}</button>
                        <button className="btn btn-sm btn-danger" onClick={async () => { await rejectSale(s.id, "Admin", "admin"); load(); }}>{t("common.reject_short")}</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {tab === "cancel" && (
        <div className="card">
          {cancelReqs.length === 0 ? (
            <div style={{ color: "#aaa", textAlign: "center", padding: 24 }}>{t("sales.no_cancel_reqs")}</div>
          ) : (
            <table className="table-card">
              <thead><tr><th>{t("sales.promoter")}</th><th>{t("sales.model")}</th><th>{t("sales.serial")}</th><th>{t("x.month")}</th><th>{t("sales.cancel_reason")}</th><th></th></tr></thead>
              <tbody>
                {cancelReqs.map(s => {
                  const locked = !isMonthEditable(s.month);
                  return (
                    <tr key={s.id}>
                      <td data-label="Promoter">{s.promoterName}</td>
                      <td data-label="Model">{s.model}</td>
                      <td data-label="Serial" style={{ fontFamily: "monospace", fontSize: 12 }}>{s.serial}</td>
                      <td data-label="Tháng" style={{ fontSize: 12, color: "#888" }}>{s.month}</td>
                      <td data-label="Lý do" style={{ fontSize: 12 }}>{s.cancelReason || "—"}</td>
                      <td data-label="">
                        <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
                          {locked && <span className="badge badge-gray">{t("sales.locked_badge")}</span>}
                          <button className="btn btn-primary btn-sm" onClick={() => handleApproveCancel(s)}>{t("sales.approve_cancel")}</button>
                          <button className="btn btn-sm btn-danger" onClick={() => handleRejectCancel(s)}>{t("sales.reject_cancel")}</button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}

      {tab === "detail" && (
        <div className="card">
          <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 12, flexWrap: "wrap" }}>
            <input
              placeholder={t("sales.search_ph")}
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{ flex: 1, minWidth: 220, fontSize: 13 }}
            />
            <span style={{ fontSize: 12, color: "#888" }}>{sortedDetail.length}/{salesList.length} {t("sales.orders_unit")}</span>
            <button className="btn btn-sm" onClick={exportSalesCSV} disabled={salesList.length === 0}>
              ⬇ {t("common.export")}
            </button>
          </div>
          <table className="table-card">
            <thead>
              <tr>
                <th style={{ cursor: "pointer" }} onClick={() => sortToggle("promoterName")}>{t("sales.promoter")}{sortArrow("promoterName")}</th>
                <th style={{ cursor: "pointer" }} onClick={() => sortToggle("model")}>{t("sales.model")}{sortArrow("model")}</th>
                <th>{t("sales.serial")}</th>
                <th>{t("sales.highend")}</th>
                <th style={{ cursor: "pointer" }} onClick={() => sortToggle("saleDate")}>{t("sales.sale_date")}{sortArrow("saleDate")}</th>
                <th>{t("sales.approver")}</th>
              </tr>
            </thead>
            <tbody>
              {sortedDetail.map(s => (
                <tr key={s.id}>
                  <td data-label="Promoter">{s.promoterName}</td>
                  <td data-label="Model">{s.model}</td>
                  <td data-label="Serial" style={{ fontFamily: "monospace", fontSize: 12 }}>{s.serial}</td>
                  <td data-label="Highend">
                    <button
                      className={"badge " + (s.isHighend ? "badge-blue" : "badge-gray")}
                      onClick={() => handleToggleHighend(s)}
                      title={t("sales.toggle_he")}
                      style={{ cursor: "pointer", border: "none" }}
                    >{s.isHighend ? t("sales.he_yes") : t("sales.he_no")}</button>
                  </td>
                  <td data-label="Ngày bán">{s.saleDate}</td>
                  <td data-label="Người duyệt">
                    {s.approvedBy
                      ? <><span style={{ fontWeight: 500 }}>{s.approvedBy}</span><br/><span style={{ fontSize: 11, color: "#888" }}>{s.approverRole === "supervisor" ? "Supervisor" : "Admin"}</span></>
                      : <span style={{ color: "#aaa" }}>—</span>}
                    {isMonthEditable(s.month) && (
                      <div style={{ marginTop: 4 }}>
                        <button className="btn btn-sm" style={{ fontSize: 11, padding: "2px 6px", color: "#b45309", borderColor: "#fde68a" }}
                          onClick={() => handleRevertApproval(s)} title={t("sales.revert_hint")}>
                          {t("sales.revert")}
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
              {salesList.length === 0 && (
                <tr><td colSpan={6} style={{ color: "#aaa", textAlign: "center", padding: 24 }}>{t("sales.no_data_month")}</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}
